// libraries
import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid, Box, Typography, Tabs, Tab } from '@mui/material'
import { useTheme } from '@mui/styles'
import { MoreVert as MoreVertIcon, Edit as EditIcon, Archive as ArchiveIcon } from '@mui/icons-material_modern'
import { useDispatch } from 'react-redux'

// services
import { useUpdatePriceSheetMutation, useArchivePriceSheetMutation } from 'services/priceSheets'

// specific components
import PriceListMaterials from 'components/containers/commodities/price-lists/components/PriceListMaterials'
import PriceListCustomers from 'components/containers/commodities/price-lists/components/PriceListCustomers'

// generic components
import SelectButton from 'components/generic/buttons/SelectButton'
import Loading from 'components/generic/Loading'
import CreatePriceListModal from 'components/generic/modals/dialog-modal/CreatePriceListModal'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

// helpers and utils
import { dispatchSnackbar } from 'utils/helpers'
import { useContentHeight } from 'utils/custom-hooks/useContentHeight'

const PriceListsSubContainer = ({ selectedPriceList, priceSheetList }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [openModal, setOpenModal] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [materialFormIsDirty, setMaterialFormIsDirty] = useState(false)
  const dispatch = useDispatch()
  const { contentHeight } = useContentHeight()
  const [updatePriceSheet, { isFetching: isFetchingUpdatePriceSheet }] = useUpdatePriceSheetMutation()
  const [archivePriceSheet, { isFetching: isFetchingArchivePriceSheet }] = useArchivePriceSheetMutation()

  useEffect(() => {
    setTabValue(0)
  }, [selectedPriceList])

  const onArchivePriceList = async () => {
    await archivePriceSheet({ priceListId: selectedPriceList.id })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Price list archived successfully.')
      })
      .catch(error => {
        const errorMessage = error?.data?.error || 'An error occurred while archiving the price list.'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const getMenuActionsOptions = () => {
    return [
      {
        id: 'rename-price-list',
        title: 'Rename',
        action: 'rename-price-list',
        onClick: () => setOpenModal(true),
        icon: <EditIcon />,
      },
      {
        id: 'archive-price-list',
        title: 'Archive',
        action: 'archive-price-list',
        onClick: () => setShowCloseModal(true),
        icon: <ArchiveIcon />,
      },
    ]
  }

  const onSave = useCallback(
    async priceListName => {
      setOpenModal(false)
      const { id: priceSheetId, baseName, type } = selectedPriceList
      await updatePriceSheet({
        priceSheetId,
        priceSheet: {
          id: priceSheetId,
          displayName: priceListName,
          baseName,
          type,
          entriesObj: [],
        },
      })
        .unwrap()
        .then(() => {
          dispatchSnackbar(dispatch, 'success', 'Price list updated successfully.')
        })
        .catch(error => {
          const errorMessage = error?.data?.error || 'An error occurred while updating the price list.'
          dispatchSnackbar(dispatch, 'error', errorMessage)
        })
    },
    [selectedPriceList],
  )

  const isLoading = isFetchingUpdatePriceSheet || isFetchingArchivePriceSheet

  return (
    <>
      <AlertModal
        open={showCloseModal}
        modalType="close"
        title="Leave This Page"
        question="Are you sure you want to archive this price list?"
        description="Customers on the price list will receive default pricing unless added to another price list."
        confirmLabel="ARCHIVE THIS PRICE LIST"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowCloseModal(false)
        }}
        onSubmit={() => {
          onArchivePriceList()
          setShowCloseModal(false)
        }}
      />
      {isLoading && <Loading />}
      <Grid item xs={9} pl={0.5} pr={1}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            backgroundColor: 'background.default',
            height: contentHeight,
            overflowY: 'auto',
          }}
          pt={2}
        >
          {selectedPriceList?.id ? (
            <>
              <Box
                p={2}
                data-testid="price-list-header"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 'fit-content',
                  padding: `
                  ${theme.spacing(3)}
                  ${theme.spacing(3)}
                  ${theme.spacing(2)}
                  ${theme.spacing(3)}
                `,
                }}
              >
                <Typography variant="h6" sx={{ flex: 1, pr: 2 }}>
                  {selectedPriceList?.displayName && `${selectedPriceList?.displayName} Price List`}
                </Typography>
                <SelectButton
                  text=""
                  variant="text"
                  color="primary"
                  endIcon={<MoreVertIcon />}
                  options={getMenuActionsOptions()}
                  disabled={materialFormIsDirty}
                  onClick={option => option.onClick()}
                />
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue || 0}
                  variant="fullWidth"
                  onChange={(event, newValue) => {
                    setTabValue(newValue)
                  }}
                >
                  <Tab label="PRICING" value={0} sx={{ width: '240px', textTransform: 'none' }} />
                  <Tab label="CUSTOMERS" value={1} sx={{ width: '240px', textTransform: 'none' }} />
                </Tabs>
              </Box>
              {tabValue === 0 && (
                <PriceListMaterials
                  selectedPriceList={selectedPriceList}
                  setMaterialFormIsDirty={setMaterialFormIsDirty}
                />
              )}
              {tabValue === 1 && <PriceListCustomers selectedPriceList={selectedPriceList} />}
            </>
          ) : (
            <Grid pt={4} display="flex" justifyContent="center">
              <Typography color="text.secondary" variant="body1">
                Select a price list to get started
              </Typography>
            </Grid>
          )}
        </Box>
      </Grid>
      <CreatePriceListModal
        title="Rename Price List"
        buttonTitle="Rename"
        hideDividers
        open={openModal}
        onSave={onSave}
        onClose={() => setOpenModal(false)}
        priceLists={priceSheetList}
        isUpdate
        currentDisplayName={selectedPriceList.displayName}
      />
    </>
  )
}

PriceListsSubContainer.propTypes = {
  selectedPriceList: PropTypes.object,
  priceSheetList: PropTypes.array,
}

PriceListsSubContainer.defaultProps = {
  selectedPriceList: {
    id: '',
  },
  priceSheetList: [],
}

export default PriceListsSubContainer
